import {UploadImageValue} from 'platform/components';
import {z} from 'zod';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  CampaignFilter,
  CompositionScene,
  WatermarkPositionEnumSchema,
  WatermarkUsageTypeSchema,
} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {VehicleSelectionMode} from '../../../types/VehicleSelectionMode';
import {compactVehicleSelectionFilter} from '../../../utils/compactVehicleSelectionFilter';
import {getDescendingVehicleSelectionRanges} from '../../../utils/getDescendingVehicleSelectionRanges';

// The uploaded image is required unless a composition is applied: the BE then renders the
// watermark file from the composition and ignores `fileId`.
export const getWatermarkFormSchema = (isVehicleSelectionEnabled: boolean) => {
  const requiredMessage = i18n.t('general.errors.mixed.required');

  return z
    .object({
      // The text input emits null when cleared.
      name: z.preprocess((value) => value ?? '', z.string().trim().min(1, requiredMessage)),
      fileId: z.custom<UploadImageValue>().nullish(),
      position: WatermarkPositionEnumSchema,
      size: z.number(),
      fitToImage: z.boolean(),
      usageType: WatermarkUsageTypeSchema,
      composition: z.custom<CompositionScene>().nullable(),
      // Automatic assignment to the matching sale vehicles.
      autoAssign: z.boolean(),
      // Which sale vehicles the watermark applies to. `filter` keeps the conditions in the "all
      // vehicles" mode too, so switching back restores them; only "by conditions" sends them.
      vehicleSelectionMode: z.nativeEnum(VehicleSelectionMode),
      filter: z.custom<CampaignFilter>(isNotNil),
    })
    .superRefine((values, ctx) => {
      if (isNil(values.composition) && isNil(values.fileId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fileId'],
          message: requiredMessage,
        });
      }

      // "By conditions" needs at least one condition and ascending ranges; the errors land on
      // the mode radio and on the upper bounds. Only the form that shows the rules validates
      // them: with the flag off a stored selection is invisible and has to stay as it is, so an
      // error on it could neither be seen nor fixed.
      const isByConditions = values.vehicleSelectionMode === VehicleSelectionMode.BY_CONDITIONS;
      if (!isVehicleSelectionEnabled || !isByConditions) {
        return;
      }
      if (isEmpty(compactVehicleSelectionFilter(values.filter))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['vehicleSelectionMode'],
          message: i18n.t('entity.watermark.validations.vehicleSelectionRequired'),
        });
      }
      getDescendingVehicleSelectionRanges(values.filter).forEach((toField) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['filter', toField],
          message: i18n.t('general.validations.fromTimeMustBeBeforeToTime'),
        });
      });
    });
};
